import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';

/**
 * Dialog mode:
 *   - `reload`         — legacy two-button layout (Dismiss / Reload). Used
 *                        when the change is strictly a local-refresh concern.
 *   - `return-or-stay` — post-submission regeneration variant. The filing's
 *                        core settings were just committed and the backend is
 *                        regenerating downstream data, so the current view is
 *                        inherently stale. Primary action sends the user back
 *                        to the files list; secondary keeps them on the page
 *                        with the same freeze behaviour the caller applies on
 *                        plain `dismiss` (validators stripped, submit gated).
 */
export type ReloadPromptMode = 'reload' | 'return-or-stay';

/**
 * Shared reload prompt modal used when an upstream cross-tab / same-tab
 * change affects the current form's layout and the only safe remediation
 * is a page reload (so the component re-reads fresh scope at init).
 *
 * Plain HTML + Tailwind + PrimeNG ButtonModule — deliberately NOT backed by
 * PrimeNG ConfirmDialog / ConfirmationService so the MBRS custom-popup
 * look-and-feel (see delete-conversion, user-signout) stays consistent.
 */
@Component({
  selector: 'app-reload-prompt',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './reload-prompt.component.html',
  styleUrls: ['./reload-prompt.component.css'],
})
export class ReloadPromptComponent {
  @Input() visible = false;
  @Input() title = 'Reload required';
  @Input() message = '';
  @Input() mode: ReloadPromptMode = 'reload';

  @Output() reload = new EventEmitter<void>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() returnToFiles = new EventEmitter<void>();

  onReloadClick(): void {
    this.reload.emit();
  }

  onDismissClick(): void {
    this.dismiss.emit();
  }

  onReturnToFilesClick(): void {
    this.returnToFiles.emit();
  }
}
